/**
 * bundles.js — pre-built document bundles.
 *
 * A bundle answers "I am about to do X — what do I need?" without filling in the whole intake.
 * Every `docs` id must resolve; the validator enforces it.
 */

export const BUNDLES = [
  {
    id: "starting-a-cpg-company",
    title: "Starting a CPG company",
    blurb: "Form the entity, settle ownership, and get personal IP into the company before anything else happens.",
    icon: "Building2",
    accent: "slate",
    stage: "idea",
    whyThisBundle:
      "Everything here is cheap now and expensive later. The founder who skips this stage is the founder whose acquisition gets discounted three years on because a co-founder who left in month five still holds twenty percent and the recipe was never assigned to the company.",
    docs: [
      { id: "founder-agreement", level: "required" },
      { id: "co-founder-agreement", level: "required" },
      { id: "llc-operating-agreement", level: "required" },
      { id: "corporate-bylaws", level: "situational" },
      { id: "founder-ip-assignment", level: "required" },
      { id: "founder-invention-assignment", level: "required" },
      { id: "piia", level: "recommended" },
      { id: "founder-vesting-agreement", level: "required" },
      { id: "mutual-nda", level: "required" },
      { id: "one-way-nda", level: "recommended" },
      { id: "independent-contractor-agreement", level: "recommended" },
      { id: "contractor-nda", level: "recommended" },
      { id: "cap-table-documentation", level: "recommended" },
    ],
  },
  {
    id: "developing-a-product",
    title: "Developing a product",
    blurb: "Work with formulators, labs and flavor houses without losing your formula.",
    icon: "FlaskConical",
    accent: "violet",
    stage: "development",
    whyThisBundle:
      "Ownership of what gets invented is decided before the work starts, not after it succeeds. The founder who paid a lab to develop a formula and never signed an assignment does not own the thing their business sells.",
    docs: [
      { id: "product-development-nda", level: "required" },
      { id: "product-development-agreement", level: "required" },
      { id: "formula-development-agreement", level: "required" },
      { id: "formula-ownership-agreement", level: "required" },
      { id: "formula-assignment-agreement", level: "required" },
      { id: "product-development-ip-agreement", level: "recommended" },
      { id: "flavor-house-agreement", level: "recommended" },
      { id: "ingredient-supplier-development-agreement", level: "situational" },
      { id: "stability-testing-agreement", level: "recommended" },
      { id: "shelf-life-testing-agreement", level: "recommended" },
      { id: "sample-testing-agreement", level: "situational" },
      { id: "trade-secret-protection-agreement", level: "recommended" },
    ],
  },
  {
    id: "hiring-a-co-packer",
    title: "Hiring a co-packer",
    blurb: "The manufacturing relationship your entire business depends on.",
    icon: "Factory",
    accent: "amber",
    stage: "manufacturing",
    whyThisBundle:
      "Read the termination and transition clauses before the pricing. Pricing you can renegotiate later; being unable to move production, or discovering you own no formula and no specifications, you cannot.",
    docs: [
      { id: "manufacturing-nda", level: "required" },
      { id: "co-packer-nda", level: "recommended" },
      { id: "co-packer-agreement", level: "required" },
      { id: "contract-manufacturing-agreement", level: "situational" },
      { id: "quality-agreement", level: "required" },
      { id: "product-specification-sheet", level: "required" },
      { id: "packaging-specification-agreement", level: "recommended" },
      { id: "recall-responsibility-agreement", level: "required" },
      { id: "food-safety-agreement", level: "recommended" },
      { id: "manufacturing-change-control-agreement", level: "recommended" },
      { id: "formula-ownership-agreement", level: "required" },
      { id: "ingredient-supply-agreement", level: "recommended" },
      { id: "supplier-quality-agreement", level: "recommended" },
      { id: "pilot-run-agreement", level: "situational" },
    ],
  },
  {
    id: "launching-into-distribution",
    title: "Launching into distribution",
    blurb: "Distributors, brokers, wholesale terms and the warehouse behind them.",
    icon: "Truck",
    accent: "blue",
    stage: "distribution",
    whyThisBundle:
      "This is where a signature can cost you a region for years. An exclusive territory with no performance minimums and no clean way out follows the brand into every future conversation, including an acquisition.",
    docs: [
      { id: "beverage-distributor-agreement", level: "required" },
      { id: "food-distributor-agreement", level: "required" },
      { id: "dsd-distributor-agreement", level: "situational" },
      { id: "exclusive-distribution-agreement", level: "situational" },
      { id: "non-exclusive-distribution-agreement", level: "recommended" },
      { id: "territory-distribution-agreement", level: "recommended" },
      { id: "cpg-broker-agreement", level: "recommended" },
      { id: "commission-sales-agreement", level: "recommended" },
      { id: "wholesale-supply-agreement", level: "required" },
      { id: "retail-vendor-agreement", level: "required" },
      { id: "promotional-allowance-agreement", level: "recommended" },
      { id: "three-pl-agreement", level: "required" },
      { id: "transportation-agreement", level: "recommended" },
      { id: "notice-to-cure", level: "recommended" },
    ],
  },
  {
    id: "raising-capital",
    title: "Raising capital",
    blurb: "SAFEs, notes and priced rounds — with counsel, not instead of counsel.",
    icon: "TrendingUp",
    accent: "green",
    stage: "fundraising",
    attorneyLed: true,
    whyThisBundle:
      "Selling equity or convertible instruments is a regulated activity. This bundle exists so you understand what you are signing and arrive at your attorney prepared — not so you can paper a round yourself.",
    docs: [
      { id: "investor-nda", level: "situational" },
      { id: "term-sheet", level: "required" },
      { id: "safe", level: "required" },
      { id: "convertible-note", level: "required" },
      { id: "preferred-stock-purchase-agreement", level: "recommended" },
      { id: "subscription-agreement", level: "recommended" },
      { id: "investor-rights-agreement", level: "recommended" },
      { id: "voting-agreement", level: "recommended" },
      { id: "rofr-co-sale-agreement", level: "recommended" },
      { id: "cap-table-documentation", level: "required" },
      { id: "founder-stock-documents", level: "required" },
      { id: "board-observer-agreement", level: "situational" },
    ],
  },
  {
    id: "building-a-sales-team",
    title: "Building a sales team",
    blurb: "Employees, reps and commissions — and the tail that outlives them.",
    icon: "Users",
    accent: "rose",
    stage: "growth",
    whyThisBundle:
      "The two things that cause fights: which accounts are house accounts, and what happens to commission on reorders after someone leaves. Both are cheap to define on day one.",
    docs: [
      { id: "employment-agreement", level: "required" },
      { id: "offer-letter", level: "required" },
      { id: "independent-sales-rep-agreement", level: "required" },
      { id: "commission-agreement", level: "required" },
      { id: "sales-employee-compensation-agreement", level: "recommended" },
      { id: "key-account-sales-agreement", level: "situational" },
      { id: "employee-confidentiality-agreement", level: "required" },
      { id: "employee-ip-assignment-agreement", level: "required" },
      { id: "separation-agreement", level: "situational" },
    ],
  },
  {
    id: "marketing-a-cpg-brand",
    title: "Marketing a CPG brand",
    blurb: "Agencies, creators and content — and owning what they make.",
    icon: "Megaphone",
    accent: "pink",
    stage: "growth",
    whyThisBundle:
      "The most expensive gap in CPG marketing is buying a post instead of an asset. Organic use, paid-ad use and whitelisting are three separate grants. A winning creative you cannot scale as paid media is worth almost nothing.",
    docs: [
      { id: "marketing-agency-agreement", level: "required" },
      { id: "branding-agency-agreement", level: "situational" },
      { id: "packaging-design-agreement", level: "required" },
      { id: "design-ownership-agreement", level: "required" },
      { id: "packaging-artwork-ownership", level: "required" },
      { id: "photography-agreement", level: "required" },
      { id: "videography-agreement", level: "recommended" },
      { id: "content-release", level: "required" },
      { id: "model-release", level: "recommended" },
      { id: "influencer-agreement", level: "required" },
      { id: "ugc-creator-agreement", level: "required" },
      { id: "content-creator-agreement", level: "recommended" },
      { id: "ambassador-agreement", level: "situational" },
      { id: "affiliate-agreement", level: "recommended" },
      { id: "influencer-disclosure", level: "required" },
    ],
  },
  {
    id: "selling-the-company",
    title: "Selling the company",
    blurb: "What diligence will ask for, and what a buyer discounts for.",
    icon: "Briefcase",
    accent: "zinc",
    stage: "exit",
    attorneyLed: true,
    whyThisBundle:
      "Buyers discount for missing paper. Unassigned IP, undocumented equity, a distributor agreement you cannot terminate and a co-packer relationship with no formula ownership all come out of the purchase price. Most of this bundle is preparation, not drafting.",
    docs: [
      { id: "due-diligence-nda", level: "required" },
      { id: "letter-of-intent", level: "required" },
      { id: "no-shop-agreement", level: "required" },
      { id: "asset-purchase-agreement", level: "required" },
      { id: "ma-stock-purchase-agreement", level: "recommended" },
      { id: "membership-interest-purchase-agreement", level: "recommended" },
      { id: "brand-acquisition-agreement", level: "recommended" },
      { id: "ip-purchase-agreement", level: "required" },
      { id: "transition-services-agreement", level: "required" },
      { id: "earn-out-agreement", level: "situational" },
      { id: "cap-table-documentation", level: "required" },
    ],
  },
];

export const BUNDLE_BY_ID = Object.fromEntries(BUNDLES.map((b) => [b.id, b]));
export const BUNDLE_IDS = BUNDLES.map((b) => b.id);
